import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/* ─── HOOKS ─────────────────────────────────────────────────────── */
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);
  return [ref, v];
}

function Reveal({ children, delay = 0, y = 32, style = {} }) {
  const [ref, v] = useInView();
  return (
    <div ref={ref} style={{
      opacity: v ? 1 : 0,
      transform: v ? "none" : `translateY(${y}px)`,
      transition: `opacity .8s ${delay}s cubic-bezier(.22,1,.36,1), transform .8s ${delay}s cubic-bezier(.22,1,.36,1)`,
      ...style
    }}>
      {children}
    </div>
  );
}

/* ─── TAG ────────────────────────────────────────────────────────── */
function Tag({ children }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
      <div style={{ width: 32, height: 1, background: "var(--g)" }} />
      <span style={{ fontFamily: "var(--sans)", fontSize: 10, letterSpacing: "0.28em", color: "var(--g)", fontWeight: 500 }}>{children}</span>
    </div>
  );
}

const WelcomePage = () => {
  const [navSolid, setNavSolid] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const fn = () => { setScrollY(window.scrollY); setNavSolid(window.scrollY > 60); };
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const parallax = -scrollY * 0.16;

  return (
    <div style={{ background: "#080808", color: "#ffffff", fontFamily: "var(--sans)", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=Instrument+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :root {
          --g: #facc15;   /* yellow-400 */
          --g5: #eab308;  /* yellow-500 */
          --gl: #fde68a;  /* yellow-200 hover */
          --gb: rgba(234,179,8,.25);
          --gdim: rgba(234,179,8,.08);
          --c: #ffffff;
          --m: #9ca3af;   /* gray-400 */
          --f: rgba(255,255,255,.06);
          --serif: 'Playfair Display', Georgia, serif;
          --sans: 'Instrument Sans', sans-serif;
          --max: 1220px;
        }
        html { scroll-behavior: smooth; }
        ::selection { background: var(--gdim); color: var(--gl); }
        @keyframes fadeUp { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:none} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: var(--gb); }
        .gl:hover { color: var(--g) !important; transition: color .25s; }
      `}</style>

      {/* ══ NAVBAR ═══════════════════════════════════════════════════ */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "nowrap",
        padding: "20px 40px",
        background: navSolid ? "rgba(8,8,8,.94)" : "transparent",
        borderBottom: navSolid ? "1px solid var(--gb)" : "1px solid transparent",
        backdropFilter: navSolid ? "blur(18px)" : "none",
        transition: "background .5s, border-color .5s, backdrop-filter .5s"
      }}>
        {/* Logo + wordmark */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <img
            src="/logo.svg"
            alt="KPL Ventures Logo"
            style={{
              width: 56,
              height: 56,
              objectFit: "contain",
              display: "block",
              filter: "brightness(0) saturate(100%) invert(82%) sepia(42%) saturate(1198%) hue-rotate(352deg) brightness(102%) contrast(96%) drop-shadow(0 0 8px rgba(250,204,21,.25))"
            }}
          />
          <span style={{ fontFamily: "var(--sans)", fontSize: 12, letterSpacing: ".2em", color: "var(--g)", fontWeight: 500, whiteSpace: "nowrap" }}>KPL VENTURES</span>
        </div>

        {/* Nav links — use onClick + scrollIntoView for iframe compatibility */}
        <div style={{ display: "flex", alignItems: "center", gap: 32, flexShrink: 0 }}>
          {user ? (
            <>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600,
                  padding: "8px 16px", background: "var(--g)", color: "#000",
                  border: "none", cursor: "pointer", whiteSpace: "nowrap",
                  transition: "background .2s, transform .2s"
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--gl)"; e.currentTarget.style.transform = "scale(1.03)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--g)"; e.currentTarget.style.transform = "none"; }}
              >
                DASHBOARD
              </button>
              <button
                onClick={async () => {
                  await logout();
                  navigate('/');
                }}
                style={{
                  fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600,
                  padding: "8px 16px", background: "transparent", color: "var(--m)",
                  border: "1px solid var(--gb)", cursor: "pointer", whiteSpace: "nowrap",
                  transition: "all .2s"
                }}
                onMouseEnter={e => { e.currentTarget.style.color = "var(--c)"; e.currentTarget.style.borderColor = "var(--g)"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "var(--m)"; e.currentTarget.style.borderColor = "var(--gb)"; }}
              >
                LOGOUT
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate('/signin')}
                style={{
                  fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600,
                  padding: "8px 16px", background: "transparent", color: "var(--m)",
                  border: "1px solid var(--gb)", cursor: "pointer", whiteSpace: "nowrap",
                  transition: "all .2s"
                }}
                onMouseEnter={e => { e.currentTarget.style.color = "var(--c)"; e.currentTarget.style.borderColor = "var(--g)"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "var(--m)"; e.currentTarget.style.borderColor = "var(--gb)"; }}
              >
                SIGN IN
              </button>
              <button
                onClick={() => navigate('/signup')}
                style={{
                  fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600,
                  padding: "8px 16px", background: "var(--g)", color: "#000",
                  border: "none", cursor: "pointer", whiteSpace: "nowrap",
                  transition: "background .2s, transform .2s"
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--gl)"; e.currentTarget.style.transform = "scale(1.03)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--g)"; e.currentTarget.style.transform = "none"; }}
              >
                SIGN UP
              </button>
            </>
          )}
        </div>
      </nav>

      {/* ══ HERO ═════════════════════════════════════════════════════ */}
      <section style={{ minHeight: "100vh", display: "flex", alignItems: "center", padding: "120px 24px 80px", position: "relative", overflow: "hidden" }}>

        {/* Subtle grid bg */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: `repeating-linear-gradient(0deg,transparent,transparent 79px,var(--f) 80px),repeating-linear-gradient(90deg,transparent,transparent 79px,var(--f) 80px)`, opacity: .5 }} />
        {/* Gold glow */}
        <div style={{ position: "absolute", top: "20%", right: "8%", width: 480, height: 480, background: "radial-gradient(ellipse,rgba(250,204,21,.06) 0%,transparent 70%)", pointerEvents: "none" }} />

        <div style={{ position: "relative", zIndex: 1, maxWidth: "var(--max)", width: "100%", margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>

          {/* LEFT */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 32, animation: "fadeUp .8s .05s both" }}>
              <div style={{ width: 8, height: 8, background: "var(--g)", animation: "pulse 2s infinite" }} />
              <span style={{ fontFamily: "var(--sans)", fontSize: 11, letterSpacing: ".28em", color: "var(--g)", fontWeight: 500 }}>WELCOME TO THE PROGRAM</span>
            </div>

            <h1 style={{ fontFamily: "var(--serif)", fontWeight: 900, lineHeight: 1.0, animation: "fadeUp .9s .1s both", marginBottom: 24 }}>
              <div style={{ fontSize: "clamp(46px, 5.5vw, 76px)", color: "var(--c)" }}>CONGRATULATIONS</div>
              <div style={{ fontSize: "clamp(46px, 5.5vw, 76px)", color: "var(--c)" }}>ON YOUR</div>
              <div style={{ fontSize: "clamp(46px, 5.5vw, 76px)", color: "var(--g5)" }}>INVESTMENT</div>
            </h1>

            <p style={{ fontFamily: "var(--sans)", fontSize: 16, color: "var(--m)", maxWidth: 460, lineHeight: 1.75, marginBottom: 40, animation: "fadeUp .9s .2s both" }}>
              You've taken the first step towards turning your idea into a real business. Your 6-week journey with KPL Ventures begins now.
            </p>

            <div style={{ display: "flex", alignItems: "center", gap: 28, animation: "fadeUp .9s .3s both" }}>
              <button onClick={() => document.getElementById("nextsteps")?.scrollIntoView({ behavior: "smooth" })} style={{
                display: "inline-flex", alignItems: "center", gap: 12,
                padding: "15px 36px", background: "var(--g)", color: "#080808",
                fontFamily: "var(--sans)", fontSize: 13, fontWeight: 700, letterSpacing: ".12em",
                border: "none", cursor: "pointer", transition: "all .25s", borderRadius: 2
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--gl)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--g)"; e.currentTarget.style.transform = "none"; }}
              >
                WHAT'S NEXT
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3L13 8L8 13M13 8H3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          {/* RIGHT - Success illustration */}
          <div style={{ animation: "fadeUp .9s .4s both" }}>
            <div style={{
              background: "rgba(250,204,21,.04)",
              border: "1px solid var(--gb)",
              borderRadius: "16px",
              padding: "60px",
              textAlign: "center",
              position: "relative"
            }}>
              <div style={{
                width: "120px",
                height: "120px",
                background: "var(--g)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 32px",
                animation: "pulse 3s infinite"
              }}>
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17L4 12" stroke="#080808" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 style={{ fontFamily: "var(--serif)", fontSize: 28, fontWeight: 700, marginBottom: 16, color: "var(--c)" }}>
                Payment Confirmed
              </h3>
              <p style={{ fontFamily: "var(--sans)", fontSize: 16, color: "var(--m)", lineHeight: 1.6 }}>
                Your AUD $5,000 investment has been successfully processed. Welcome to the KPL Ventures community!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ NEXT STEPS ═══════════════════════════════════════════════ */}
      <section id="nextsteps" style={{ padding: "120px 24px", position: "relative" }}>
        <div style={{ maxWidth: "var(--max)", width: "100%", margin: "0 auto" }}>

          <Reveal>
            <Tag>NEXT STEPS</Tag>
            <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(36px, 4vw, 56px)", fontWeight: 900, lineHeight: 1.1, marginBottom: 16 }}>
              Your Journey Begins
            </h2>
            <p style={{ fontFamily: "var(--sans)", fontSize: 18, color: "var(--m)", maxWidth: 680, lineHeight: 1.7, marginBottom: 80 }}>
              Here's what happens in the coming days as you prepare for your 6-week accelerator program.
            </p>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 48 }}>

            <Reveal delay={.1}>
              <div style={{ background: "rgba(255,255,255,.02)", border: "1px solid var(--gb)", borderRadius: "12px", padding: "40px", height: "100%" }}>
                <div style={{ fontFamily: "var(--sans)", fontSize: 12, letterSpacing: ".2em", color: "var(--g)", fontWeight: 600, marginBottom: 24 }}>WITHIN 24 HOURS</div>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: 24, fontWeight: 700, marginBottom: 16, color: "var(--c)" }}>Onboarding Package</h3>
                <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.7 }}>
                  Receive your personalized onboarding materials including program schedule, preparation resources, and access to our founder community.
                </p>
              </div>
            </Reveal>

            <Reveal delay={.2}>
              <div style={{ background: "rgba(255,255,255,.02)", border: "1px solid var(--gb)", borderRadius: "12px", padding: "40px", height: "100%" }}>
                <div style={{ fontFamily: "var(--sans)", fontSize: 12, letterSpacing: ".2em", color: "var(--g)", fontWeight: 600, marginBottom: 24 }}>WITHIN 48 HOURS</div>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: 24, fontWeight: 700, marginBottom: 16, color: "var(--c)" }}>Strategy Session</h3>
                <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.7 }}>
                  Schedule your 1:1 strategy session with our lead mentor to align on your goals and customize your 6-week journey.
                </p>
              </div>
            </Reveal>

            <Reveal delay={.3}>
              <div style={{ background: "rgba(255,255,255,.02)", border: "1px solid var(--gb)", borderRadius: "12px", padding: "40px", height: "100%" }}>
                <div style={{ fontFamily: "var(--sans)", fontSize: 12, letterSpacing: ".2em", color: "var(--g)", fontWeight: 600, marginBottom: 24 }}>WITHIN 1 WEEK</div>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: 24, fontWeight: 700, marginBottom: 16, color: "var(--c)" }}>Program Kickoff</h3>
                <p style={{ fontFamily: "var(--sans)", fontSize: 15, color: "var(--m)", lineHeight: 1.7 }}>
                  Join your cohort for the official program launch, meet your fellow founders, and begin Week 1 of intensive business building.
                </p>
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ══ WHAT TO EXPECT ═══════════════════════════════════════════ */}
      <section style={{ padding: "120px 24px", background: "var(--f)", position: "relative" }}>
        <div style={{ maxWidth: "var(--max)", width: "100%", margin: "0 auto" }}>

          <Reveal>
            <Tag>WHAT TO EXPECT</Tag>
            <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(36px, 4vw, 56px)", fontWeight: 900, lineHeight: 1.1, marginBottom: 16 }}>
              Your 6-Week Transformation
            </h2>
            <p style={{ fontFamily: "var(--sans)", fontSize: 18, color: "var(--m)", maxWidth: 680, lineHeight: 1.7, marginBottom: 80 }}>
              A structured journey from idea to investable business, guided by proven B2B expertise.
            </p>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 32 }}>

            <Reveal delay={.1}>
              <div style={{ background: "#080808", border: "1px solid var(--gb)", borderRadius: "12px", padding: "32px", height: "100%" }}>
                <div style={{ fontFamily: "var(--sans)", fontSize: 48, fontWeight: 700, color: "var(--g)", marginBottom: 16 }}>01</div>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 700, marginBottom: 12, color: "var(--c)" }}>Foundation & Validation</h3>
                <p style={{ fontFamily: "var(--sans)", fontSize: 14, color: "var(--m)", lineHeight: 1.6 }}>
                  Deep customer research, problem validation, and market analysis to ensure you're building something people need.
                </p>
              </div>
            </Reveal>

            <Reveal delay={.2}>
              <div style={{ background: "#080808", border: "1px solid var(--gb)", borderRadius: "12px", padding: "32px", height: "100%" }}>
                <div style={{ fontFamily: "var(--sans)", fontSize: 48, fontWeight: 700, color: "var(--g)", marginBottom: 16 }}>02</div>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 700, marginBottom: 12, color: "var(--c)" }}>Business Model</h3>
                <p style={{ fontFamily: "var(--sans)", fontSize: 14, color: "var(--m)", lineHeight: 1.6 }}>
                  Revenue strategy, pricing models, and go-to-market planning with real B2B sales experience.
                </p>
              </div>
            </Reveal>

            <Reveal delay={.3}>
              <div style={{ background: "#080808", border: "1px solid var(--gb)", borderRadius: "12px", padding: "32px", height: "100%" }}>
                <div style={{ fontFamily: "var(--sans)", fontSize: 48, fontWeight: 700, color: "var(--g)", marginBottom: 16 }}>03</div>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 700, marginBottom: 12, color: "var(--c)" }}>Product & Tech</h3>
                <p style={{ fontFamily: "var(--sans)", fontSize: 14, color: "var(--m)", lineHeight: 1.6 }}>
                  MVP development, tech architecture, and building with real users from day one.
                </p>
              </div>
            </Reveal>

            <Reveal delay={.4}>
              <div style={{ background: "#080808", border: "1px solid var(--gb)", borderRadius: "12px", padding: "32px", height: "100%" }}>
                <div style={{ fontFamily: "var(--sans)", fontSize: 48, fontWeight: 700, color: "var(--g)", marginBottom: 16 }}>04</div>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 700, marginBottom: 12, color: "var(--c)" }}>Launch & Scale</h3>
                <p style={{ fontFamily: "var(--sans)", fontSize: 14, color: "var(--m)", lineHeight: 1.6 }}>
                  Customer acquisition, launch strategy, and 90-day execution plan for sustainable growth.
                </p>
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* ══ CTA ═══════════════════════════════════════════════════════ */}
      <section style={{ padding: "120px 24px", position: "relative" }}>
        <div style={{ maxWidth: "var(--max)", width: "100%", margin: "0 auto", textAlign: "center" }}>

          <Reveal>
            <h2 style={{ fontFamily: "var(--serif)", fontSize: "clamp(36px, 4vw, 56px)", fontWeight: 900, lineHeight: 1.1, marginBottom: 24 }}>
              Ready to Build Your Future?
            </h2>
            <p style={{ fontFamily: "var(--sans)", fontSize: 18, color: "var(--m)", maxWidth: 600, lineHeight: 1.7, margin: "0 auto 48px" }}>
              Your journey starts now. Check your email for next steps and join our founder community.
            </p>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 24, flexWrap: "wrap" }}>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 12,
                  padding: "16px 40px", background: "var(--g)", color: "#080808",
                  fontFamily: "var(--sans)", fontSize: 14, fontWeight: 700, letterSpacing: ".12em",
                  border: "none", cursor: "pointer", transition: "all .25s", borderRadius: 2
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--gl)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--g)"; e.currentTarget.style.transform = "none"; }}
              >
                GO TO DASHBOARD
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3L13 8L8 13M13 8H3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <button
                onClick={() => navigate('/')}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 12,
                  padding: "16px 40px", background: "transparent", color: "var(--m)",
                  fontFamily: "var(--sans)", fontSize: 14, fontWeight: 600,
                  border: "1px solid var(--gb)", cursor: "pointer", transition: "all .25s", borderRadius: 2
                }}
                onMouseEnter={e => { e.currentTarget.style.color = "var(--c)"; e.currentTarget.style.borderColor = "var(--g)"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "var(--m)"; e.currentTarget.style.borderColor = "var(--gb)"; }}
              >
                BACK TO HOME
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default WelcomePage;